import clsx, { ClassValue } from 'clsx';
import { CSSProperties, HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDisableGlobalHotkeys } from '@shared/util/hotkeys';
import { getController, setControlledTimeout } from '@shared/util/timeout';
import {
  hideFloatingPanel,
  inertBackground,
  isOutsideModalClick,
  shouldCloseOnEscape,
  showFloatingPanel,
  trackAutoPopoverDismiss,
} from './stack';
import styles from './Dialog.module.css';

export interface DialogProps extends Omit<
  HTMLAttributes<HTMLDialogElement>,
  'className' | 'title'
> {
  children?: ReactNode;
  className?: ClassValue;
  style?: CSSProperties;
  isOpen?: boolean;
  lightDismiss?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onClickOutside?: () => void;
  /**
   * How the dialog enters the stack. Mutually exclusive:
   *
   * - `'standard'` (default): a non-modal `dialog.show()` rendered in normal
   *   flow and stacked by z-index. The page behind is inerted, but other
   *   top-layer UI (toasts, menus) and sibling modals stay interactive. Raises
   *   to the front on click. The standard modal — used by card modals and most
   *   surfaces.
   * - `'modal'`: `showModal()`, which enters the top layer and makes
   *   *everything* outside the dialog inert, including toasts and other
   *   top-layer UI. Typically just alerts and confirms.
   * - `'floating'`: shown as a manual popover, so it enters the top layer and
   *   sits above everything — modals and other top-layer UI alike — without
   *   inerting the background; the whole page stays live. No backdrop. For
   *   movable surfaces like the Ask panel.
   */
  mode?: 'standard' | 'modal' | 'floating';
  /**
   * Page-width sizing: the dialog takes the page width
   * (`--modal-page-min-width`, default 100Q; override per-modal via a
   * className) and expands to the full viewport width when `fullWidth` is set.
   * Height still fits the content — use this for a page-width modal that should
   * not be full height.
   */
  pageWidth?: boolean;
  /**
   * Full-page modal: `pageWidth` plus full viewport height, and a full-screen
   * layout on narrow (mobile) viewports.
   */
  fullPage?: boolean;
  /** With `pageWidth` / `fullPage`, grow the dialog to the full viewport width.
   */
  fullWidth?: boolean;
  /**
   * Full-screen modal: edge-to-edge (100dvw × 100dvh) with no border, radius,
   * shadow, or margin. For immersive surfaces like the image lightbox. Style
   * the background/chrome via a `className`.
   */
  fullScreen?: boolean;
  /**
   * Animate the open/close transition and stay mounted until the exit animation
   * finishes. Without it the dialog unmounts immediately on close, so a modal
   * with no close transition can't linger after its backdrop is gone. Pass it
   * for a modal that animates on its own, as alerts/confirms do; nested modals
   * animate regardless.
   */
  animate?: boolean;
  /**
   * Something to render behind this dialog: a decorative layer of its own, in front of everything
   * below it in the modal stack and behind this surface's content.
   *
   * Rendered in this dialog's own portal, immediately before the `<dialog>` element. That is what
   * places it: `standard` dialogs all share one z-index and stack by DOM order, so a sibling sitting
   * between two of them covers the one underneath and is covered by this one. Give it a
   * `z-index: var(--modal-zindex)` and it lands in that band; leave it `pointer-events: none`, since
   * anything below it is inert while this dialog is open and a backdrop that swallows clicks would
   * also swallow the outside click that dismisses.
   *
   * Separate from the shared `ModalBackdrop`, which is one always-mounted dimmer for the whole app
   * and sits below the entire band. This is per-surface and replaces nothing.
   */
  backdrop?: ReactNode;
  testID?: string;
}

export function Dialog({
  children,
  className,
  style,
  isOpen,
  onOpenChange,
  onClickOutside,
  lightDismiss = false,
  mode = 'standard',
  pageWidth = false,
  fullPage = false,
  fullWidth = false,
  fullScreen = false,
  animate = false,
  backdrop,
  testID,
  ...rest
}: DialogProps) {
  const isStandard = mode === 'standard';
  const isModal = mode === 'modal';
  const isFloating = mode === 'floating';
  const ref = useRef<HTMLDialogElement>(null);
  const [isMounted, setIsMounted] = useState(!!isOpen);
  const [animateWidth, setAnimateWidth] = useState(false);
  const onOpenChangeRef = useRef(onOpenChange);
  const onClickOutsideRef = useRef(onClickOutside);

  // Global hotkeys are disabled when a non-floating dialog is open.
  useDisableGlobalHotkeys(isMounted && !!isOpen && !isFloating);

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
    onClickOutsideRef.current = onClickOutside;
  });

  // Mount as soon as the modal opens
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  // Enable the width transition only after the modal has mounted, so it doesn't
  // animate its width on open
  useEffect(() => {
    if (!fullPage && !pageWidth) {
      return;
    }
    const { signal, abort } = getController();
    setControlledTimeout(() => setAnimateWidth(true), 100, signal);
    return abort;
  }, [fullPage, pageWidth]);

  // Keep the dialog in the DOM until the close transition ends
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted) {
      return;
    }

    const { signal, abort } = getController();
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== dialog) {
        return;
      }
      // Floating panels are popovers and never set `open`; `data-open` is their
      // shown-state (see showFloatingPanel).
      if (!dialog.open && !dialog.hasAttribute('data-open')) {
        setIsMounted(false);
      }
    };
    dialog.addEventListener('transitionend', handleTransitionEnd, { signal });
    return abort;
  }, [isMounted]);

  // Route dismissals (Escape, close()) through onOpenChange
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted) {
      return;
    }

    const { signal, abort } = getController();
    const requestClose = () => onOpenChangeRef.current?.(false);

    if (isModal) {
      // showModal(): Escape fires `cancel`; route it and any close() through.
      const handleCancel = (event: Event) => {
        event.preventDefault();
        requestClose();
      };
      const handleClose = () => {
        if (!dialog.open) {
          requestClose();
        }
      };
      dialog.addEventListener('cancel', handleCancel, { signal });
      dialog.addEventListener('close', handleClose, { signal });
      return abort;
    }

    // standard + floating: a non-modal dialog.show() gets no `cancel`, so close on
    // Escape manually when this dialog should handle it (see shouldCloseOnEscape).
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldCloseOnEscape(dialog, event)) {
        requestClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown, { signal });
    return abort;
  }, [isMounted, isModal]);

  // Notify clicks outside modal dialogs and light-dismiss
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted || !isModal) {
      return;
    }
    const { signal, abort } = getController();
    const wasDismissingAutoPopover = trackAutoPopoverDismiss(signal);
    // Clicks on a modal dialog's ::backdrop target the dialog element itself
    const handleClick = (event: MouseEvent) => {
      if (event.target === dialog && !wasDismissingAutoPopover()) {
        onClickOutsideRef.current?.();
        if (lightDismiss) {
          dialog.close();
        }
      }
    };
    dialog.addEventListener('click', handleClick, { signal });
    return abort;
  }, [isMounted, isModal, lightDismiss]);

  // Notify clicks outside standard modal dialogs and light-dismiss. Floating
  // surfaces opt out — they never dismiss on an outside click, so the page
  // behind stays live.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted || !isStandard) {
      return;
    }
    const { signal, abort } = getController();
    const wasDismissingAutoPopover = trackAutoPopoverDismiss(signal);
    // Outside clicks land on the shared backdrop or the inerted page behind the
    // modal, so detect them at the document level.
    const handleClick = (event: MouseEvent) => {
      if (wasDismissingAutoPopover()) {
        return;
      }
      if (isOutsideModalClick(dialog, event)) {
        onClickOutsideRef.current?.();
        if (lightDismiss) {
          dialog.close();
        }
      }
    };
    // delay adding this listener until after the dialog is opened, since the
    // click that opened the dialog can still be bubbling to the document
    setControlledTimeout(
      () => document.addEventListener('click', handleClick, { signal }),
      100,
      signal,
    );
    return abort;
  }, [isMounted, isStandard, lightDismiss]);

  // Sync the isOpen prop to the dialog's actual shown state
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted || !isOpen) {
      return;
    }

    if (isModal) {
      // Native modal: top layer, inert background and backdrop all from the UA.
      if (!dialog.open) {
        dialog.showModal();
      }
      return () => {
        if (dialog.open) {
          dialog.close();
        }
      };
    }

    // standard: a non-modal dialog rendered in normal flow, then move focus in.
    // Standard modals share one z-index and stack by DOM (open) order; they inert
    // the page behind them. floating: shown as a manual popover, so it enters the
    // top layer above the whole modal band and other top-layer UI, and leaves the
    // page live.
    const previousFocus = document.activeElement;
    if (isFloating) {
      showFloatingPanel(dialog);
    } else if (!dialog.open) {
      dialog.show();
    }
    (dialog.querySelector<HTMLElement>('[autofocus]') ?? dialog).focus();
    const inertedElements = isStandard ? inertBackground() : [];

    return () => {
      for (const element of inertedElements) {
        element.inert = false;
      }
      if (isFloating) {
        hideFloatingPanel(dialog);
      } else if (dialog.open) {
        dialog.close();
      }
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus();
      }
    };
  }, [isMounted, isOpen, isModal, isStandard, isFloating]);

  // Non-animated dialogs unmount immediately on close; animated ones stay for
  // the exit transition.
  const shouldCloseImmediately = !isOpen && !animate;
  if (!isMounted || shouldCloseImmediately) {
    return null;
  }

  // Portal every dialog into <body> so all open modals are DOM siblings. `modal`
  // uses showModal() (top layer); `standard` renders in normal flow and stacks by
  // z-index (see Dialog.module.css); `floating` is a manual popover in the top
  // layer. tabIndex lets a click on the surface's own chrome focus it so keyboard
  // focus lands inside on open.
  return createPortal(
    <>
      {backdrop}
      <dialog
        ref={ref}
        aria-modal={isStandard ? true : undefined}
        tabIndex={isModal ? undefined : -1}
        popover={isFloating ? 'manual' : undefined}
        data-floating={isFloating || undefined}
        data-modal-animate={animate || undefined}
        className={clsx(
          styles.dialog,
          (pageWidth || fullPage) && styles.pageWidth,
          fullPage && styles.fullPage,
          fullScreen && styles.fullScreen,
          fullWidth && styles.fullWidth,
          (pageWidth || fullPage) && animateWidth && styles.animateWidth,
          className,
        )}
        style={style}
        data-testid={testID}
        {...rest}
      >
        {children}
      </dialog>
    </>,
    document.body,
  );
}
